import React, { useEffect, useState } from "react";
import { Typography } from "antd";
import styles from "./schedule-routine-table.module.css";
import CustomTable from "../../../../../../../../../common/custom-table/custom-table";
import SVGIcon from "../../../../../../../../../constants/SVGIcon";
import CustomPopover from "../../../../../../../../../common/custom-popover/custom-popover";
import { EyeOutlined, LeftOutlined, PlusOutlined } from "@ant-design/icons";
import { fetchActivityRoutine } from "../../../../../../../../../services/school_info";
import { toast } from "react-toastify";
import { deleteClass } from "../../../../../../../../../services/class_routine";
import CustomButton from "../../../../../../../../../common/custom-button/custom-button";

const ScheduleRoutineTable = ({ 
  classData, 
  onBack, 
  onEdit,
  onCreate,
  handleOnClose
}) => {
  const [routineList, setRoutineList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const hasActiveAcademicYear = routineList?.some(
    (item) => item?.academic_year?.active_year
  );

  useEffect(() => {
    setPageNo(1);
    getActivityRoutines();
  }, [classData?.id]);

  const getActivityRoutines = async (
    tempPage = pageNo,
    tempPageSize = pageSize
  ) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
      };
      const response = await fetchActivityRoutine(classData?.id, payload);
      if (response?.data) {
        const data = response?.data?.data?.routines?.map((item) => ({
          ...item,
          level_id: classData?.level?.id,
          classroom_id: classData?.id,
        }));
        setRoutineList(data);
        setTotalCount(response?.data?.data?.totalRecords);
        return data;
      }
      return [];
    } catch (err) {
      console.log(err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoutine = async (id) => {
    try {
      setLoading(true);
      await deleteClass(id);
      toast.success("Routine Deleted Successfully!");
      handleOnClose();
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || "Failed to delete routine. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getActivityRoutines(page, pageSize);
  };

  const columns = [
    {
      title: "Academic Year",
      dataIndex: "academic_year",
      key: "academic_year",
      render: (value) => value?.name || "-",
    },
    {
      title: "Routine Name",
      dataIndex: "routine_name",
      key: "routine_name",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => {
        const isActiveYear = record?.academic_year?.active_year;
        const isReadOnlyView = hasActiveAcademicYear && !isActiveYear;
        const canDelete = isActiveYear;

        return (
          <div className={styles.actionItemWrapper}>
            {isReadOnlyView  ? (
              <>
                <EyeOutlined
                  onClick={() => onEdit({ ...record, isReadOnly: true })}
                  style={{ color: "#1E9EFD", fontSize: 24 }}
                />
                <SVGIcon
                  name="delete"
                  width="20"
                  height="20"
                  stroke="rgba(0, 0, 0, 0.25)"
                  style={{ cursor: "not-allowed" }}
                />
              </>
            ) : (
              <>
                <SVGIcon
                  name={"edit-icon"}
                  width={"20"}
                  height={"20"}
                  stroke={"#3BB44B"}
                  onClick={() => onEdit(record)}
                />
                {canDelete ? (
                  <CustomPopover
                    message="Are you sure you want to delete this schedule?"
                    confirmText="Delete"
                    cancelText="Cancel"
                    onConfirm={() => handleDeleteRoutine(record?.routine_id)}
                  >
                    <SVGIcon
                      name={"delete"}
                      width={"20"}
                      height={"20"}
                      stroke={"rgba(243, 17, 17, 1)"}
                    />
                  </CustomPopover>
                ) : (
                  <SVGIcon
                    name="delete"
                    width="20"
                    height="20"
                    stroke="rgba(0, 0, 0, 0.25)"
                    style={{ cursor: "not-allowed" }}
                  />
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className={styles.titleContainer}>
        <div className={styles.leftSection} onClick={onBack}>
          <LeftOutlined />
          <Typography className={styles.navTitle}>
            {classData?.level?.name} - {classData?.name}
          </Typography>
        </div>

        <CustomButton
          title="Add New Schedule and Activity"
          size="large"
          icon={<PlusOutlined />}
          onClick={onCreate}
        />
      </div>

      <CustomTable
        columns={columns}
        dataSource={routineList}
        headerColor="#667085"
        headerBackgroundColor="#F9FAFB"
        loading={loading}
        total={totalCount}
        pageSize={pageSize}
        currentPage={pageNo}
        handlePageChange={handleChangePage}
      />
    </div>
  );
};

export default ScheduleRoutineTable;
